import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch,
    RadioGroup,
    Radio,
    TextField,
    Chip,
    Divider,
    Fab,
    IconButton,
    Tooltip,
} from '@mui/material';
import { PlayCircle, Lightbulb, TrendingUp, Settings, Send, CheckCircle } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useReading } from '@hooks/useReading';
import WaveformDisplay from './WaveformDisplay';
import AudioComparison from './AudioComparison';
import EnhancedAnalytics from './EnhancedAnalytics';
import EnhancedRecommendations from './EnhancedRecommendations';
import WordHighlightedText from './WordHighlightedText';
import DetailedSpeechFeedback from './DetailedSpeechFeedback';
import AudioRecorder, { AudioRecorderRef } from '@components/common/AudioRecorder';
import type { ReadingAttempt, FeedbackTone } from '@services/reading/types';
import { ArrowForward, ArrowBack, Compare, Assessment } from '@mui/icons-material';

interface ReadingPracticeInterfaceProps {
    sessionId?: string;
    passageId?: string;
}

interface SentenceHighlight {
    index: number;
    isActive: boolean;
    isCompleted: boolean;
    pronunciationScore?: number;
    fluencyScore?: number;
    attempt?: ReadingAttempt;
}

const ReadingPracticeInterface: React.FC<ReadingPracticeInterfaceProps> = ({
    sessionId: propSessionId,
    passageId: propPassageId,
}) => {
    const { passageId: paramPassageId, sessionId: paramSessionId } = useParams<{
        passageId: string;
        sessionId: string;
    }>();
    const navigate = useNavigate();

    const finalPassageId = propPassageId || paramPassageId;
    const finalSessionId = propSessionId || paramSessionId;

    const {
        readingState,
        sessionsState,
        fetchReadingPassageById,
        fetchPassageQuestions,
        startReadingSession,
        fetchReadingSessionById,
        uploadAudio,
        submitComprehension,
        fetchSessionFeedback,
        generateSessionFeedback,
        completeReadingSession,
        isLoading,
    } = useReading();

    // === COMPONENT STATE ===
    const [currentView, setCurrentView] = useState<'reading' | 'questions' | 'results'>('reading');
    const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
    const [sentences, setSentences] = useState<string[]>([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [sentenceHighlights, setSentenceHighlights] = useState<SentenceHighlight[]>([]);
    const [selectedAnswers, setSelectedAnswers] = useState<{ [questionId: string]: string }>({});
    const [feedbackTone, setFeedbackTone] = useState<FeedbackTone>('encouraging');
    const [showSettings, setShowSettings] = useState(false);
    const [currentAttemptFeedback, setCurrentAttemptFeedback] = useState<ReadingAttempt | null>(null);
    const [enableRealTimeFeedback, setEnableRealTimeFeedback] = useState(true);
    const [enableWaveform, setEnableWaveform] = useState(true);
    const [autoPlayNext, setAutoPlayNext] = useState(false);
    // New enhanced component states
    const [showAudioComparison, setShowAudioComparison] = useState(false);
    const [selectedAttemptForComparison, setSelectedAttemptForComparison] = useState<ReadingAttempt | null>(null);
    const [showEnhancedAnalytics, setShowEnhancedAnalytics] = useState(false);
    const [showEnhancedRecommendations, setShowEnhancedRecommendations] = useState(false);
    const [showDetailedFeedback, setShowDetailedFeedback] = useState(false);
    const [selectedAttemptForFeedback, setSelectedAttemptForFeedback] = useState<ReadingAttempt | null>(null);

    // === REFS ===
    const audioRecorderRef = useRef<AudioRecorderRef>(null);
    const audioElementRef = useRef<HTMLAudioElement | null>(null);

    // === COMPUTED VALUES ===
    const currentSession = sessionsState.currentSession;
    const currentPassage = readingState.currentPassage;
    const comprehensionQuestions = readingState.currentPassageQuestions || [];
    const sessionAttempts = sessionsState.sessionAttempts || [];

    const readingProgress = useMemo(() => {
        if (!sentences.length) return 0;
        const completedSentences = sentenceHighlights.filter((h) => h.isCompleted).length;
        return (completedSentences / sentences.length) * 100;
    }, [sentences.length, sentenceHighlights]);

    const overallScore = useMemo(() => {
        if (!sessionAttempts.length) return 0;
        const avgPronunciation =
            sessionAttempts.reduce((sum, attempt) => sum + (attempt.pronunciation_score || 0), 0) /
            sessionAttempts.length;
        const avgFluency =
            sessionAttempts.reduce((sum, attempt) => sum + (attempt.fluency_score || 0), 0) / sessionAttempts.length;
        return Math.round((avgPronunciation + avgFluency) / 2);
    }, [sessionAttempts]);

    // Get current sentence attempt for word highlighting
    const currentSentenceAttempt = useMemo(() => {
        return sessionAttempts.find((attempt) => attempt.sentence_number === currentSentenceIndex + 1);
    }, [sessionAttempts, currentSentenceIndex]);

    // === INITIALIZATION ===
    useEffect(() => {
        if (finalPassageId && !currentPassage) {
            fetchReadingPassageById(finalPassageId);
            fetchPassageQuestions(finalPassageId);
        }
    }, [finalPassageId, currentPassage]);

    useEffect(() => {
        if (finalSessionId && !currentSession) {
            fetchReadingSessionById(finalSessionId);
        }
    }, [finalSessionId, currentSession]);

    useEffect(() => {
        if (currentPassage && !finalSessionId) {
            // Start new session
            startReadingSession({
                passage_id: currentPassage.id,
                feedback_tone: feedbackTone,
                enable_realtime_feedback: enableRealTimeFeedback,
                enable_waveform_display: enableWaveform,
            });
        }
    }, [currentPassage, finalSessionId, feedbackTone, enableRealTimeFeedback, enableWaveform]);

    useEffect(() => {
        if (currentPassage?.content) {
            const passageSentences = currentPassage.content
                .split(/[.!?]+/)
                .filter((sentence) => sentence.trim().length > 0)
                .map((sentence) => sentence.trim() + '.');

            setSentences(passageSentences);
            setSentenceHighlights(
                passageSentences.map((_, index) => ({
                    index,
                    isActive: index === 0,
                    isCompleted: false,
                })),
            );
        }
    }, [currentPassage?.content]);

    // === AUDIO RECORDING HANDLERS ===
    const handleRecordingStop = useCallback(
        ({ audioBlob, audioDuration }: { audioBlob: Blob; audioDuration: number }) => {
            if (currentSession && sentences[currentSentenceIndex]) {
                // Convert Blob to File
                const audioFile = new File([audioBlob], `recording-${Date.now()}.webm`, {
                    type: audioBlob.type || 'audio/webm',
                });
                
                uploadAudio({
                    session_id: currentSession.id,
                    sentence_number: currentSentenceIndex + 1,
                    audio_file: audioFile,
                    audio_duration: audioDuration,
                    target_sentence: sentences[currentSentenceIndex],
                });
            }
        },
        [currentSession, sentences, currentSentenceIndex, uploadAudio],
    );

    // Update sentence highlights when new attempts are added
    useEffect(() => {
        if (sessionAttempts.length > 0) {
            setSentenceHighlights((prev) =>
                prev.map((highlight, index) => {
                    const attempt = sessionAttempts.find((a) => a.sentence_number === index + 1);
                    if (attempt) {
                        const isCompleted =
                            (attempt.pronunciation_score || 0) >= 60 && (attempt.fluency_score || 0) >= 60;
                        return {
                            ...highlight,
                            isCompleted,
                            pronunciationScore: attempt.pronunciation_score,
                            fluencyScore: attempt.fluency_score,
                            attempt, // Store the attempt for word highlighting
                        };
                    }
                    return highlight;
                }),
            );

            // Set current attempt feedback for real-time display
            const latestAttempt = sessionAttempts[sessionAttempts.length - 1];
            if (latestAttempt && latestAttempt.sentence_number === currentSentenceIndex + 1) {
                setCurrentAttemptFeedback(latestAttempt);

                // Auto-advance if passing and auto-play is enabled
                const isPassing =
                    (latestAttempt.pronunciation_score || 0) >= 60 && (latestAttempt.fluency_score || 0) >= 60;
                if (isPassing && autoPlayNext && currentSentenceIndex < sentences.length - 1) {
                    setTimeout(() => {
                        handleNextSentence();
                    }, 1500);
                }
            }
        }
    }, [sessionAttempts, currentSentenceIndex, autoPlayNext, sentences.length]);

    // === COMPREHENSION HANDLERS ===
    const handleAnswerChange = useCallback((questionId: string, answer: string) => {
        setSelectedAnswers((prev) => ({
            ...prev,
            [questionId]: answer,
        }));
    }, []);

    const handleSubmitComprehension = useCallback(async () => {
        if (!currentSession) return;

        const answers = comprehensionQuestions.map((question) => ({
            question_id: question.id,
            answer: selectedAnswers[question.id] || '',
            time_taken_seconds: 60, // This could be tracked more precisely
        }));

        await submitComprehension({
            session_id: currentSession.id,
            answers,
        });

        setCurrentView('results');
        handleCompleteReading();
    }, [currentSession, comprehensionQuestions, selectedAnswers, submitComprehension]);

    const handleCompleteReading = useCallback(async () => {
        if (currentSession) {
            await completeReadingSession(currentSession.id);
            await generateSessionFeedback(currentSession.id);
            await fetchSessionFeedback(currentSession.id);
        }
    }, [currentSession, completeReadingSession, generateSessionFeedback, fetchSessionFeedback]);

    // === NAVIGATION HANDLERS ===
    const handleNextSentence = useCallback(() => {
        if (currentSentenceIndex < sentences.length - 1) {
            const newIndex = currentSentenceIndex + 1;
            setCurrentSentenceIndex(newIndex);

            setSentenceHighlights((prev) =>
                prev.map((highlight, index) => ({
                    ...highlight,
                    isActive: index === newIndex,
                })),
            );

            if (autoPlayNext) {
                // Auto-start recording for next sentence
                setTimeout(() => audioRecorderRef.current?.startRecording(), 500);
            }
        } else {
            // Handle last sentence - check if we should go to questions or complete
            if (comprehensionQuestions.length > 0) {
                setCurrentView('questions');
            } else {
                // Mark the current sentence as active but completed
                setSentenceHighlights((prev) =>
                    prev.map((highlight, index) => ({
                        ...highlight,
                        isActive: index === currentSentenceIndex,
                        isCompleted: index === currentSentenceIndex ? true : highlight.isCompleted,
                    })),
                );

                // Move to results view and complete reading
                setCurrentView('results');
                handleCompleteReading();
            }
        }
    }, [currentSentenceIndex, sentences.length, comprehensionQuestions.length, autoPlayNext]);

    const handlePreviousSentence = useCallback(() => {
        if (currentSentenceIndex > 0) {
            const newIndex = currentSentenceIndex - 1;
            setCurrentSentenceIndex(newIndex);

            setSentenceHighlights((prev) =>
                prev.map((highlight, index) => ({
                    ...highlight,
                    isActive: index === newIndex,
                })),
            );
        }
    }, [currentSentenceIndex]);

    const handleJumpToSentence = useCallback((index: number) => {
        setCurrentSentenceIndex(index);
        setSentenceHighlights((prev) =>
            prev.map((highlight, i) => ({
                ...highlight,
                isActive: i === index,
            })),
        );
    }, []);

    // === UI HANDLERS ===

    const handleViewResults = useCallback(() => {
        if (currentSession) {
            navigate(`/reading/session/${currentSession.id}/results`);
        }
    }, [currentSession, navigate]);

    // === NEW ENHANCED COMPONENT HANDLERS ===
    const handleOpenAudioComparison = useCallback((attempt: ReadingAttempt) => {
        // Ensure we have a valid attempt with an ID
        if (!attempt || !attempt.id) {
            console.error('Cannot open audio comparison: invalid attempt', attempt);
            return;
        }
        setSelectedAttemptForComparison(attempt);
        setShowAudioComparison(true);
    }, []);

    const handleCloseAudioComparison = useCallback(() => {
        setShowAudioComparison(false);
        setSelectedAttemptForComparison(null);
    }, []);

    const handleOpenEnhancedAnalytics = useCallback(() => {
        setShowEnhancedAnalytics(true);
    }, []);

    const handleOpenEnhancedRecommendations = useCallback(() => {
        setShowEnhancedRecommendations(true);
    }, []);

    // === NEW DETAILED FEEDBACK HANDLERS ===
    const handleOpenDetailedFeedback = useCallback((attempt: ReadingAttempt) => {
        if (!attempt || !attempt.id) {
            console.error('Cannot open detailed feedback: invalid attempt', attempt);
            return;
        }
        setSelectedAttemptForFeedback(attempt);
        setShowDetailedFeedback(true);
    }, []);

    const handleCloseDetailedFeedback = useCallback(() => {
        setShowDetailedFeedback(false);
        setSelectedAttemptForFeedback(null);
    }, []);

    // === WORD HIGHLIGHTING HANDLERS ===
    const handleWordClick = useCallback((word: string, score: number) => {
        console.log(`Clicked word: ${word}, Score: ${score}%`);
        // You can add more functionality here, like showing word-specific feedback
    }, []);

    // === RENDER HELPERS ===
    const renderSentenceWithHighlight = (sentence: string, index: number) => {
        const highlight = sentenceHighlights[index];
        const attempt = sessionAttempts.find((a) => a.sentence_number === index + 1);

        return (
            <Paper
                key={index}
                onClick={() => handleJumpToSentence(index)}
                sx={{
                    p: 2,
                    mb: 1,
                    cursor: 'pointer',
                    backgroundColor: highlight.isActive
                        ? 'primary.light'
                        : highlight.isCompleted
                        ? 'success.light'
                        : 'background.paper',
                    border: highlight.isActive ? 2 : 1,
                    borderColor: highlight.isActive
                        ? 'primary.main'
                        : highlight.isCompleted
                        ? 'success.main'
                        : 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: 2,
                        transform: 'translateY(-1px)',
                    },
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    {/* Use WordHighlightedText if we have analysis data */}
                    {attempt && attempt.word_scores && attempt.word_scores.length > 0 ? (
                        <WordHighlightedText
                            text={sentence}
                            wordScores={attempt.word_scores}
                            onClick={handleWordClick}
                        />
                    ) : (
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: highlight.isActive ? 'bold' : 'normal',
                                color: highlight.isActive ? 'primary.contrastText' : 'text.primary',
                            }}
                        >
                            {sentence}
                        </Typography>
                    )}

                    <Box display="flex" alignItems="center" gap={1}>
                        {attempt && (
                            <>
                                {attempt.pronunciation_score !== undefined && (
                                    <Chip
                                        label={`P: ${Math.round(attempt.pronunciation_score)}%`}
                                        size="small"
                                        color={
                                            attempt.pronunciation_score >= 80
                                                ? 'success'
                                                : attempt.pronunciation_score >= 60
                                                ? 'warning'
                                                : 'error'
                                        }
                                    />
                                )}
                                {attempt.fluency_score !== undefined && (
                                    <Chip
                                        label={`F: ${Math.round(attempt.fluency_score)}%`}
                                        size="small"
                                        color={
                                            attempt.fluency_score >= 80
                                                ? 'success'
                                                : attempt.fluency_score >= 60
                                                ? 'warning'
                                                : 'error'
                                        }
                                    />
                                )}
                                <Tooltip title="View detailed analysis">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenDetailedFeedback(attempt);
                                        }}
                                        sx={{ color: 'info.main' }}
                                    >
                                        <Assessment sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Compare audio with reference">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenAudioComparison(attempt);
                                        }}
                                        sx={{ color: 'primary.main' }}
                                    >
                                        <Compare sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}

                        {highlight.isCompleted && <CheckCircle size={20} color="green" />}
                        {highlight.isActive && <PlayCircle size={20} color="blue" />}
                    </Box>
                </Box>
            </Paper>
        );
    };

    const renderReadingView = () => (
        <Box>
            {/* Header */}
            <Box mb={3}>
                <Typography variant="h4" gutterBottom>
                    {currentPassage?.title}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                    <Chip label={currentPassage?.difficulty} color="primary" />
                    <Chip label={currentPassage?.cefr_level} variant="outlined" />
                    <Chip label={`${currentPassage?.word_count} words`} variant="outlined" />
                    <Chip label={currentPassage?.estimated_time_display} variant="outlined" />
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={readingProgress}
                    sx={{ height: 8, borderRadius: 4, mb: 2 }}
                />

                <Typography variant="body2" color="text.secondary">
                    Progress: {Math.round(readingProgress)}% | Sentence {currentSentenceIndex + 1} of {sentences.length}
                </Typography>
            </Box>

            {/* Main Content */}
            <Box display="flex" gap={3} sx={{ flexDirection: { xs: 'column', lg: 'row' } }}>
                {/* Reading Panel */}
                <Box flex={2}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Reading Passage
                        </Typography>

                        <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {sentences.map((sentence, index) => renderSentenceWithHighlight(sentence, index))}
                        </Box>
                    </Paper>

                    {/* Recording Controls */}
                    <Paper sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="h6">
                                Current Sentence ({currentSentenceIndex + 1}/{sentences.length})
                            </Typography>

                            <Box display="flex" gap={1}>
                                <Tooltip title="Previous">
                                    <IconButton onClick={handlePreviousSentence} disabled={currentSentenceIndex === 0}>
                                        <ArrowBack />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Next">
                                    <IconButton
                                        onClick={handleNextSentence}
                                        disabled={currentSentenceIndex === sentences.length - 1}
                                    >
                                        <ArrowForward />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                            {currentSentenceAttempt &&
                            currentSentenceAttempt.word_scores &&
                            currentSentenceAttempt.word_scores.length > 0 ? (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Pronunciation Analysis (click words for details):
                                    </Typography>
                                    <WordHighlightedText
                                        text={sentences[currentSentenceIndex]}
                                        wordScores={currentSentenceAttempt.word_scores}
                                        onClick={handleWordClick}
                                    />
                                </Box>
                            ) : (
                                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                                    {sentences[currentSentenceIndex]}
                                </Typography>
                            )}
                        </Paper>

                        <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                            <AudioRecorder
                                ref={audioRecorderRef}
                                onRecordingStop={handleRecordingStop}
                                disabled={isLoading}
                                size="large"
                                showPlayback={false}
                                maxDuration={60}
                            />
                        </Box>
                    </Paper>
                </Box>

                {/* Side Panel */}
                <Box flex={1}>
                    {/* Waveform Display */}
                    {enableWaveform && (
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Audio Visualization
                            </Typography>
                            <WaveformDisplay
                                isRecording={audioRecorderRef.current?.isRecording || false}
                                attempt={sessionAttempts.find((a) => a.sentence_number === currentSentenceIndex + 1)}
                            />
                        </Paper>
                    )}

                    {/* Real-time Feedback */}
                    {enableRealTimeFeedback && currentAttemptFeedback && (
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                                <Lightbulb size={20} />
                                Instant Feedback
                            </Typography>

                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography>Pronunciation</Typography>
                                    <Typography fontWeight="bold">
                                        {Math.round(currentAttemptFeedback.pronunciation_score || 0)}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={currentAttemptFeedback.pronunciation_score || 0}
                                    color="primary"
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                            </Box>

                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography>Fluency</Typography>
                                    <Typography fontWeight="bold">
                                        {Math.round(currentAttemptFeedback.fluency_score || 0)}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={currentAttemptFeedback.fluency_score || 0}
                                    color="secondary"
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                            </Box>

                            {/* Word-level feedback preview */}
                            {currentAttemptFeedback.word_scores && currentAttemptFeedback.word_scores.length > 0 && (
                                <Box mb={2}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Word Scores:
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                        {currentAttemptFeedback.word_scores.slice(0, 6).map((wordScore, index) => (
                                            <Chip
                                                key={index}
                                                label={`${wordScore.word}: ${wordScore.quality_score}%`}
                                                size="small"
                                                color={
                                                    wordScore.quality_score >= 80
                                                        ? 'success'
                                                        : wordScore.quality_score >= 60
                                                        ? 'warning'
                                                        : 'error'
                                                }
                                                sx={{ fontSize: '0.7rem' }}
                                            />
                                        ))}
                                        {currentAttemptFeedback.word_scores.length > 6 && (
                                            <Chip
                                                label={`+${currentAttemptFeedback.word_scores.length - 6} more`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem' }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Test score equivalencies */}
                            {(currentAttemptFeedback.ielts_pronunciation_score ||
                                currentAttemptFeedback.cefr_pronunciation_level ||
                                currentAttemptFeedback.speechace_pronunciation_score) && (
                                <Box mb={2}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Test Equivalencies:
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                        {currentAttemptFeedback.ielts_pronunciation_score && (
                                            <Chip
                                                label={`IELTS: Band ${currentAttemptFeedback.ielts_pronunciation_score}`}
                                                size="small"
                                                color={
                                                    currentAttemptFeedback.ielts_pronunciation_score >= 7
                                                        ? 'success'
                                                        : currentAttemptFeedback.ielts_pronunciation_score >= 6
                                                        ? 'warning'
                                                        : 'error'
                                                }
                                                variant="outlined"
                                            />
                                        )}
                                        {currentAttemptFeedback.cefr_pronunciation_level && (
                                            <Chip
                                                label={`CEFR: ${currentAttemptFeedback.cefr_pronunciation_level}`}
                                                size="small"
                                                color={
                                                    ['C1', 'C2'].includes(
                                                        currentAttemptFeedback.cefr_pronunciation_level,
                                                    )
                                                        ? 'success'
                                                        : ['B1', 'B2'].includes(
                                                              currentAttemptFeedback.cefr_pronunciation_level,
                                                          )
                                                        ? 'warning'
                                                        : 'error'
                                                }
                                                variant="outlined"
                                            />
                                        )}
                                        {currentAttemptFeedback.speechace_pronunciation_score && (
                                            <Chip
                                                label={`SpeechAce: ${currentAttemptFeedback.speechace_pronunciation_score}`}
                                                size="small"
                                                color={
                                                    currentAttemptFeedback.speechace_pronunciation_score >= 70
                                                        ? 'success'
                                                        : currentAttemptFeedback.speechace_pronunciation_score >= 50
                                                        ? 'warning'
                                                        : 'error'
                                                }
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Enhanced action button */}
                            <Box display="flex" gap={1} mt={2}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleOpenDetailedFeedback(currentAttemptFeedback)}
                                    startIcon={<Assessment sx={{ fontSize: 14 }} />}
                                >
                                    View Details
                                </Button>

                                {currentAttemptFeedback.audio_url && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleOpenAudioComparison(currentAttemptFeedback)}
                                        startIcon={<Compare sx={{ fontSize: 14 }} />}
                                    >
                                        Compare
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                    )}

                    {/* Progress Summary */}
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                            <TrendingUp size={20} />
                            Session Progress
                        </Typography>

                        <Box textAlign="center" mb={2}>
                            <Typography variant="h3" color="primary" fontWeight="bold">
                                {overallScore}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Overall Score
                            </Typography>
                        </Box>

                        <Box mb={2}>
                            <Typography variant="body2" gutterBottom>
                                Sentences Completed: {sentenceHighlights.filter((h) => h.isCompleted).length}/
                                {sentences.length}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={readingProgress}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => {
                                if (comprehensionQuestions.length > 0) {
                                    setCurrentView('questions');
                                } else {
                                    handleCompleteReading();
                                }
                            }}
                            disabled={sentenceHighlights.filter((h) => h.isCompleted).length < sentences.length}
                            startIcon={<Send />}
                            sx={{ mb: 2 }}
                        >
                            {comprehensionQuestions.length > 0 ? 'Continue to Questions' : 'Complete Reading'}
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleOpenEnhancedAnalytics}
                            startIcon={<Assessment sx={{ fontSize: 16 }} />}
                        >
                            View Enhanced Analytics
                        </Button>
                    </Paper>
                </Box>
            </Box>
        </Box>
    );

    const renderComprehensionView = () => (
        <Box>
            <Typography variant="h4" gutterBottom>
                Comprehension Questions
            </Typography>

            <Typography variant="body1" color="text.secondary" mb={3}>
                Answer the following questions based on the passage you just read.
            </Typography>

            {comprehensionQuestions.map((question, index) => (
                <Paper key={question.id} sx={{ p: 3, mb: 3 }}>
                    <Box mb={2}>
                        <Typography variant="h6" gutterBottom>
                            Question {index + 1}
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {question.question_text}
                        </Typography>

                        <Box display="flex" gap={1} mb={2}>
                            <Chip label={question.difficulty_level} size="small" />
                            <Chip label={`${question.points} points`} variant="outlined" size="small" />
                        </Box>
                    </Box>

                    {question.question_type === 'multiple_choice' && (
                        <RadioGroup
                            value={selectedAnswers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        >
                            {question.answer_options.map((option, optionIndex) => (
                                <FormControlLabel key={optionIndex} value={option} control={<Radio />} label={option} />
                            ))}
                        </RadioGroup>
                    )}

                    {question.question_type === 'true_false' && (
                        <RadioGroup
                            value={selectedAnswers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        >
                            <FormControlLabel value="true" control={<Radio />} label="True" />
                            <FormControlLabel value="false" control={<Radio />} label="False" />
                        </RadioGroup>
                    )}

                    {(question.question_type === 'short_answer' || question.question_type === 'fill_blank') && (
                        <TextField
                            fullWidth
                            multiline={question.question_type === 'short_answer'}
                            rows={question.question_type === 'short_answer' ? 3 : 1}
                            value={selectedAnswers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            placeholder="Type your answer here..."
                        />
                    )}
                </Paper>
            ))}

            <Box display="flex" gap={2} justifyContent="center" mt={4}>
                <Button variant="outlined" onClick={() => setCurrentView('reading')} startIcon={<ArrowBack />}>
                    Back to Reading
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmitComprehension}
                    disabled={comprehensionQuestions.some((q) => !selectedAnswers[q.id])}
                    startIcon={<Send />}
                >
                    Submit Answers
                </Button>
            </Box>
        </Box>
    );

    const renderResultsView = () => (
        <Box textAlign="center">
            <Typography variant="h4" gutterBottom color="primary">
                🎉 Great Job!
            </Typography>

            <Typography variant="h6" color="text.secondary" mb={4}>
                You've completed the reading session
            </Typography>

            <Box display="flex" justifyContent="center" gap={3} mb={4}>
                <Paper sx={{ p: 3, minWidth: 120 }}>
                    <Typography variant="h3" color="primary" fontWeight="bold">
                        {overallScore}%
                    </Typography>
                    <Typography variant="body2">Overall Score</Typography>
                </Paper>

                <Paper sx={{ p: 3, minWidth: 120 }}>
                    <Typography variant="h3" color="success.main" fontWeight="bold">
                        {sentenceHighlights.filter((h) => h.isCompleted).length}
                    </Typography>
                    <Typography variant="body2">Sentences Read</Typography>
                </Paper>
            </Box>

            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap" mb={4}>
                <Button variant="outlined" onClick={() => navigate('/reading')}>
                    Back to Library
                </Button>

                <Button variant="contained" onClick={handleViewResults}>
                    View Detailed Results
                </Button>

                <Button
                    variant="outlined"
                    onClick={handleOpenEnhancedAnalytics}
                    startIcon={<Assessment sx={{ fontSize: 16 }} />}
                >
                    Enhanced Analytics
                </Button>

                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleOpenEnhancedRecommendations}
                    startIcon={<Lightbulb size={16} />}
                >
                    Get AI Recommendations
                </Button>
            </Box>

            {/* Quick recommendations preview */}
            <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                    🎯 What's Next?
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Based on your performance, here are some suggestions:
                </Typography>
                <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                    {overallScore >= 80 && <Chip label="Try a harder difficulty level" color="success" size="small" />}
                    {overallScore < 60 && (
                        <Chip label="Practice similar difficulty passages" color="warning" size="small" />
                    )}
                    <Chip label="Focus on pronunciation" color="info" size="small" />
                    <Chip label="Work on reading speed" color="secondary" size="small" />
                </Box>
            </Paper>
        </Box>
    );

    // === SETTINGS DIALOG ===
    const renderSettingsDialog = () => (
        <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Reading Practice Settings</DialogTitle>
            <DialogContent>
                <Box py={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enableRealTimeFeedback}
                                onChange={(e) => setEnableRealTimeFeedback(e.target.checked)}
                            />
                        }
                        label="Enable Real-time Feedback"
                    />

                    <FormControlLabel
                        control={
                            <Switch checked={enableWaveform} onChange={(e) => setEnableWaveform(e.target.checked)} />
                        }
                        label="Show Audio Waveform"
                    />

                    <FormControlLabel
                        control={<Switch checked={autoPlayNext} onChange={(e) => setAutoPlayNext(e.target.checked)} />}
                        label="Auto-record Next Sentence"
                    />

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" gutterBottom>
                        Feedback Tone
                    </Typography>
                    <RadioGroup value={feedbackTone} onChange={(e) => setFeedbackTone(e.target.value as FeedbackTone)}>
                        <FormControlLabel value="encouraging" control={<Radio />} label="Encouraging" />
                        <FormControlLabel value="constructive" control={<Radio />} label="Constructive" />
                        <FormControlLabel value="detailed" control={<Radio />} label="Detailed" />
                        <FormControlLabel value="brief" control={<Radio />} label="Brief" />
                    </RadioGroup>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowSettings(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    if (isLoading && !currentPassage) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Box textAlign="center">
                        <Typography variant="h6" gutterBottom>
                            Loading reading session...
                        </Typography>
                        <LinearProgress sx={{ width: 200, mt: 2 }} />
                    </Box>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Floating Action Buttons */}
            <Box position="fixed" bottom={24} right={24} zIndex={1000}>
                <Box display="flex" flexDirection="column" gap={1}>
                    {sessionAttempts.length > 0 && (
                        <>
                            <Tooltip title="Detailed Speech Analysis">
                                <Fab
                                    color="secondary"
                                    size="small"
                                    onClick={() => {
                                        const latestAttempt = sessionAttempts[sessionAttempts.length - 1];
                                        if (latestAttempt && latestAttempt.id) {
                                            handleOpenDetailedFeedback(latestAttempt);
                                        }
                                    }}
                                >
                                    <Assessment sx={{ fontSize: 20 }} />
                                </Fab>
                            </Tooltip>

                            <Tooltip title="Compare Latest Recording">
                                <Fab
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                        const latestAttempt = sessionAttempts[sessionAttempts.length - 1];
                                        if (latestAttempt && latestAttempt.id) {
                                            handleOpenAudioComparison(latestAttempt);
                                        }
                                    }}
                                >
                                    <Compare sx={{ fontSize: 20 }} />
                                </Fab>
                            </Tooltip>
                        </>
                    )}

                    <Tooltip title="Enhanced Analytics">
                        <Fab color="info" size="small" onClick={handleOpenEnhancedAnalytics}>
                            <Assessment sx={{ fontSize: 20 }} />
                        </Fab>
                    </Tooltip>

                    <Tooltip title="Settings">
                        <Fab color="secondary" onClick={() => setShowSettings(true)}>
                            <Settings />
                        </Fab>
                    </Tooltip>
                </Box>
            </Box>
            {/* Main Content */}
            {currentView === 'reading' && renderReadingView()}
            {currentView === 'questions' && renderComprehensionView()}
            {currentView === 'results' && renderResultsView()}
            {/* Settings Dialog */}
            {renderSettingsDialog()}
            {/* Audio Comparison Dialog */}
            <Dialog
                open={showAudioComparison}
                onClose={handleCloseAudioComparison}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '80vh' },
                }}
            >
                <DialogTitle>
                    Audio Comparison Analysis
                    <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={handleCloseAudioComparison}>
                        ×
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedAttemptForComparison && (
                        <AudioComparison attempt={selectedAttemptForComparison} onClose={handleCloseAudioComparison} />
                    )}
                </DialogContent>
            </Dialog>
            {/* Enhanced Analytics Dialog */}
            <Dialog
                open={showEnhancedAnalytics}
                onClose={() => setShowEnhancedAnalytics(false)}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '90vh' },
                }}
            >
                <DialogTitle>
                    Enhanced Reading Analytics
                    <IconButton
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => setShowEnhancedAnalytics(false)}
                    >
                        ×
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <EnhancedAnalytics />
                </DialogContent>
            </Dialog>
            {/* Enhanced Recommendations Dialog */}
            <Dialog
                open={showEnhancedRecommendations}
                onClose={() => setShowEnhancedRecommendations(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '80vh' },
                }}
            >
                <DialogTitle>
                    AI-Powered Reading Recommendations
                    <IconButton
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => setShowEnhancedRecommendations(false)}
                    >
                        ×
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <EnhancedRecommendations />
                </DialogContent>
            </Dialog>
            {/* Detailed Feedback Dialog */}
            <Dialog
                open={showDetailedFeedback}
                onClose={handleCloseDetailedFeedback}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '80vh' },
                }}
            >
                <DialogTitle>
                    Detailed Speech Analysis
                    <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={handleCloseDetailedFeedback}>
                        ×
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedAttemptForFeedback && (
                        <DetailedSpeechFeedback
                            attempt={selectedAttemptForFeedback}
                            onClose={handleCloseDetailedFeedback}
                        />
                    )}
                </DialogContent>
            </Dialog>
            Hidden Audio Element for Playback
            <audio ref={audioElementRef} style={{ display: 'none' }} />
        </Container>
    );
};

export default ReadingPracticeInterface;
